import type { OrderUpdateAPI } from '@hero/api';
import { getObjectId } from '@hero/schema';
import type { Fee, FeeType, Id, PaymentMethod } from '@hero/schema';

/**
 * Input shape consumed by {@link buildUpdateOrderPricePayload}.
 * Mirrors the slice of `OrderDraftState` an edit drawer carries:
 * the resolved load price, the fee list (excluding auto fees like
 * `items_insurance`), the taxable flag, and the optional payment
 * method. `orderId` + `contractId` identify the persisted target.
 */
export interface BuildUpdateOrderPricePayloadInput {
  orderId: Id;
  contractId: Id;
  loadPrice: number;
  isTaxable: boolean;
  fees: Array<{ type: FeeType; amount: number }>;
  paymentMethod?: PaymentMethod;
}

/**
 * Edit-mode wire-format builder — the symmetric pair of
 * {@link buildCreateMultiOrderPayload} for persisted-order edits.
 *
 * Keeps the OrderPriceEditDrawer save logic in sync with the
 * publish builder: the same `(loadPrice, fees, taxable)` triple
 * produces an `OrderUpdateAPI` body via this function and a
 * `CreateMultiOrderAPI['req']['routes'][i].price` via the create
 * builder. A change to either schema only requires editing one
 * function on each side.
 */
export const buildUpdateOrderPricePayload = (
  input: BuildUpdateOrderPricePayloadInput,
): OrderUpdateAPI['req'] => ({
  _id: getObjectId(input.orderId),
  contractId: getObjectId(input.contractId),
  beneficiaryAmount: {
    itemsOriginalSubtotal: input.loadPrice,
    taxable: input.isTaxable,
    fees: input.fees.map(f => ({ type: f.type, amount: f.amount })) as Fee[],
  },
  paymentMethod: input.paymentMethod,
});
